// frontend/src/utils/pwaDisplayMode.js

import { useEffect, useState } from 'react';

/**
 * "Is this document already running as the installed app?"
 *
 * Single source of truth for the install-affordance gate. Every surface that
 * offers to install the PWA (the top install banner, the awareness snackbar,
 * the navbar menu item, the notifications settings block) must be suppressed
 * inside the installed app - telling a user who is already in the app to
 * install the app is a bug, not a nudge.
 *
 * Two signals, on purpose:
 *   - `(display-mode: standalone)` covers Chrome/Edge/Android and desktop PWAs.
 *     The manifest declares `"display": "standalone"`, so this is the mode our
 *     installed app actually runs in.
 *   - `navigator.standalone` is the iOS/Safari home-screen flag, which is the
 *     ONLY signal available there (iOS does not report the display-mode media
 *     query for home-screen apps on older versions).
 *
 * Fail-open by design: when neither signal is available we assume "browser",
 * so the worst case is showing an install nudge to someone who cannot use it -
 * never hiding the app's own install path from someone who needs it.
 */

const STANDALONE_QUERY = '(display-mode: standalone)';

export const isStandalonePwa = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.(STANDALONE_QUERY)?.matches === true ||
    window.navigator?.standalone === true
  );
};

/**
 * "Is this an iOS / iPadOS device?"
 *
 * SINGLE SOURCE OF TRUTH, and it lives here rather than in either consumer
 * because the two must never disagree: PushEnableNudge decides whether to show
 * the "install the app first" variant, and InstallInstructionsDialog decides
 * whether to render the iOS step list or the two-platform desktop one. When the
 * card used a wider test than the dialog, an iPadOS user was told to install and
 * then handed DESKTOP instructions.
 *
 * Two clauses, on purpose:
 *   - the UA regex covers iPhone / iPod and every iPad up to iPadOS 12;
 *   - iPadOS 13+ Safari reports itself as a Mac, so it is only identifiable by
 *     the pairing of the `MacIntel` platform with a touch screen. Desktop Macs
 *     report `maxTouchPoints === 0` (a connected trackpad does not count), and
 *     touch-screen Windows laptops report `Win32`, so neither is a false hit.
 *
 * Wrapped because `navigator` is absent under SSR and some hardened browsers
 * throw on `maxTouchPoints`. Fail-closed to "not iOS" - the desktop/Android
 * instructions are the safer default for an unknown device.
 */
export const isIosDevice = () => {
  try {
    if (typeof navigator === 'undefined') return false;
    if (/iphone|ipad|ipod/i.test(navigator.userAgent)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  } catch {
    return false;
  }
};

/**
 * Android, in any browser. Fail-closed to "not Android".
 *
 * @returns {boolean}
 */
export const isAndroidDevice = () => {
  try {
    if (typeof navigator === 'undefined') return false;
    return /android/i.test(navigator.userAgent);
  } catch {
    return false;
  }
};

/**
 * A computer OS: Windows, ChromeOS, a desktop Mac (an iPadOS Mac-alike is
 * caught by `isIosDevice` first) or desktop Linux. Android reports "Linux" too,
 * so it is excluded explicitly.
 */
const isDesktopOs = () => {
  try {
    if (typeof navigator === 'undefined') return false;
    if (isAndroidDevice() || isIosDevice()) return false;
    return /windows nt|cros|macintosh|x11|linux/i.test(navigator.userAgent);
  } catch {
    return false;
  }
};

const hasCoarsePointer = () => {
  try {
    return Boolean(window.matchMedia?.('(pointer: coarse)')?.matches);
  } catch {
    return false;
  }
};

/**
 * May the TOP install banner (`InstallPrompt`) offer itself on this device?
 * Phones and tablets only (owner decision 2026-11-03: no install ask on a
 * computer): Android always - it is the one platform whose browser hands us
 * a real one-tap installer - and otherwise only a touch-first device
 * (`pointer: coarse`) that is not running a computer OS. A narrow desktop or
 * Chromebook Chrome window fires `beforeinstallprompt` and fits the banner's
 * width gate, so width alone let it show there; a touch-screen laptop or a
 * Chromebook in tablet mode is still a computer. Fails closed (no banner).
 *
 * @returns {boolean}
 */
export const isMobileInstallDevice = () => {
  if (isAndroidDevice()) return true;
  return hasCoarsePointer() && !isDesktopOs();
};

/**
 * Reactive form of `isStandalonePwa` for components that stay mounted across
 * a display-mode change (the navbar lives for the whole session). Subscribes
 * to the media query so the menu entry disappears the moment the document is
 * running standalone, instead of going stale until the next full reload.
 */
export const useIsStandalonePwa = () => {
  const [standalone, setStandalone] = useState(isStandalonePwa);

  useEffect(() => {
    const mq = window.matchMedia?.(STANDALONE_QUERY);
    if (!mq) return undefined;

    const handleChange = () => setStandalone(isStandalonePwa());

    // Safari < 14 only has the deprecated addListener/removeListener pair.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handleChange);
      return () => mq.removeEventListener('change', handleChange);
    }
    mq.addListener(handleChange);
    return () => mq.removeListener(handleChange);
  }, []);

  return standalone;
};
